import { registerComponent, debounce, getComponentConfig } from "helpers";

registerComponent(
  "wyatt-slider",
  class WyattSlider extends HTMLElement {
    constructor() {
      super();

      this.config = getComponentConfig(this);
      this.currentIndex = 0;
      this.slider = this.querySelector("[data-slider]");
      this.slides = Array.from(this.slider.children);
      this.firstSlideOffset = 0;
      this.slideCount = this.slides.length;
      this.slidesToShow =
        this.config.slidesToShow || this.calculateSlidesToShow();
      this.showControls = this.config.hideControls
        ? false
        : this.slideCount > 1 && this.slideCount > this.slidesToShow;
    }

    connectedCallback() {
      this.setupData();
      this.addControls();
      this.addNavigation();
      this.bindEvents();
      this.connectSliders();

      this.goTo(this.config.startAt, "instant");
      this.setAttribute("ready", true);
    }

    // SETUP

    setupData() {
      this.slides.forEach((slide, index) => {
        slide.dataset.slide = index;
        slide.classList.add("slider__slide");
      });
    }

    addControls() {
      if (!this.showControls) {
        return;
      }

      const template = this.querySelector("[data-controls]").content;
      const controls = document.importNode(template, true);
      this.appendChild(controls);

      this.setAttribute("has-controls", true);
      this.controlButtons = this.querySelectorAll(
        "[data-slider-control-button]",
      );
    }

    addNavigation() {
      if (!this.config.showNavigation) {
        return;
      }

      const navigation = document.createElement("div");
      const navigationButton = this.querySelector(
        "[data-navigation-button]",
      ).content;

      this.slides.forEach((slide, index) => {
        const button = navigationButton.cloneNode(true);

        button.querySelector(".slider__dot").textContent =
          this.getPaginationButtonText(index);
        button.querySelector(
          "[data-slider-navigation-button]",
        ).dataset.sliderNavigationButton = index;

        if (index === this.currentIndex) {
          button
            .querySelector("[data-slider-navigation-button]")
            .setAttribute("active", true);
        }

        navigation.appendChild(button);
      });

      navigation.classList.add("slider__navigation");
      this.appendChild(navigation);

      this.navigationButtons = navigation.querySelectorAll(
        "[data-slider-navigation-button]",
      );
    }

    bindEvents() {
      this.slider.addEventListener(
        "scroll",
        debounce((event) => {
          this.handleScroll(event);
        }, 300).bind(this),
      );

      if (this.showControls) {
        this.controlButtons.forEach((button) => {
          button.addEventListener("click", this.handleControlClick.bind(this));
        });
      }

      if (this.config.showNavigation) {
        this.navigationButtons.forEach((button) => {
          button.addEventListener(
            "click",
            this.handleNavigationClick.bind(this),
          );
        });
      }
    }

    connectSliders() {
      const sliderToNavigate = document.querySelector(
        this.config.navigationFor,
      );

      if (sliderToNavigate) {
        this.sliderToNavigate = sliderToNavigate;
        sliderToNavigate.navigatedBy = this;

        this.slides.forEach((slide) => {
          this.updateSlideAsNavigation(slide);

          slide
            .querySelector("button")
            .addEventListener(
              "click",
              this.handleSlideAsNavigationClick.bind(this),
            );
        });
      }
    }

    updateSlideAsNavigation(slide) {
      const button = document.createElement("button");
      const index = parseInt(slide.dataset.slide, 10);

      button.type = "button";
      button.classList.add("button-reset", "slider__slide-button");
      button.setAttribute(
        "aria-label",
        slide.dataset.mediaAlt || this.getPaginationButtonText(index),
      );
      button.innerHTML = slide.innerHTML;

      slide.innerHTML = "";
      slide.appendChild(button);
    }

    getPaginationButtonText(index) {
      return this.config.paginationButtonText.replace(
        "{{ number }}",
        index + 1,
      );
    }

    // HANDLERS

    handleControlClick(event) {
      const button = event.currentTarget;
      const direction = button.dataset.sliderControlButton;
      const newIndex =
        direction === "next" ? this.currentIndex + 1 : this.currentIndex - 1;

      this.updateState(newIndex);
      this.goTo(newIndex);

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
      }

      if (this.sliderToNavigate) {
        this.sliderToNavigate.goTo(newIndex);
      }
    }

    handleNavigationClick(event) {
      const button = event.currentTarget;
      const newIndex = parseInt(button.dataset.sliderNavigationButton, 10);

      this.updateState(newIndex);
      this.goTo(newIndex);
    }

    handleScroll() {
      /*
        scrollLeft and offsetWidth round the actual values, therefor a buffer is needed
        to ensure the position will be slightly larger than the multiples of slideWidth
      */
      const buffer = this.slideCount;
      const position = this.slider.scrollLeft;
      const slideWidth = this.slider.offsetWidth / this.slidesToShow;
      const newIndex = Math.ceil((position - buffer) / slideWidth);

      if (position === this.firstSlideOffset) {
        return;
      }

      this.firstSlideOffset = position;

      if (this === this.sliderToNavigate?.navigatedBy) {
        this.updateControlButtons(newIndex);
      } else {
        this.updateState(newIndex);
      }

      if (this.navigatedBy) {
        this.navigatedBy.goTo(newIndex);
        this.navigatedBy.updateCurrentSlide(newIndex);
      }
    }

    handleSlideAsNavigationClick(event) {
      const slide = event.currentTarget.closest("[data-slide]");
      const index = parseInt(slide.dataset.slide, 10);

      event.preventDefault();

      this.updateState(index);
      this.sliderToNavigate.goTo(index);
    }

    // UPDATES

    updateState(newIndex) {
      this.updateSliderClass(newIndex);
      this.updateNavigationButtons(newIndex);
      this.updateCurrentSlide(newIndex);
      this.updateControlButtons(newIndex);
    }

    updateSliderClass(newIndex) {
      this.classList.remove(`slider--at-slide-${this.currentIndex}`);
      this.classList.add(`slider--at-slide-${newIndex}`);
    }

    updateNavigationButtons(newIndex) {
      if (!this.config.showNavigation) {
        return;
      }

      const currentButton = this.querySelector(
        `[data-slider-navigation-button="${this.currentIndex}"]`,
      );
      const newButton = this.querySelector(
        `[data-slider-navigation-button="${newIndex}"]`,
      );

      currentButton.setAttribute("active", false);
      newButton.setAttribute("active", true);
    }

    updateCurrentSlide(newIndex) {
      this.slides[this.currentIndex].setAttribute("active", false);
      this.currentIndex = newIndex;
      this.slides[this.currentIndex].setAttribute("active", true);
    }

    updateControlButtons(newIndex) {
      if (!this.showControls) {
        return;
      }

      this.controlButtons.forEach((button) => {
        const onFirstSlide = this.slider.scrollLeft === 0 || newIndex === 0;
        const onLastSlide =
          this.slider.scrollLeft + this.slider.offsetWidth >=
            this.slider.scrollWidth || newIndex === this.slideCount - 1;

        if (onFirstSlide && button.dataset.sliderControlButton === "previous") {
          button.disabled = true;
        } else if (
          onLastSlide &&
          button.dataset.sliderControlButton === "next"
        ) {
          button.disabled = true;
        } else {
          button.disabled = false;
        }
      });
    }

    goTo(newIndex, behavior = "smooth") {
      const newSlide = this.slider.querySelector(`[data-slide="${newIndex}"]`);
      const width = newSlide.offsetWidth;

      this.slider.scrollTo({
        top: 0,
        left: width * newIndex,
        behavior,
      });
    }

    calculateSlidesToShow() {
      const slideWidth = this.slider.children[0].offsetWidth;
      const containerWidth = this.slider.offsetWidth;
      return containerWidth / slideWidth;
    }
  },
);
